import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import {
    ActionPreviewEdits,
    ActionPreviewRecipientsPeopleSection,
} from '../../../../../services/gable/gable.service';
import { PeoplePickerComponent } from '../../../shared/people-picker/people-picker.component';
import {
    PickerPerson,
    PickerSelection,
} from '../../../shared/people-picker/people-picker.types';

/**
 * People-picker variant of the recipients section. Renders `app-people-picker`
 * (multi-select + free-text email; self-loads the org's people) and diffs the
 * selection against the pre-selected `items` into the shared recipients edit
 * envelope — `person-{id}` for member selections, `email:{addr}` for free-text.
 */
@Component({
    selector: 'app-ap-recipients-people-section',
    templateUrl: './recipients-people-section.component.html',
    styleUrls: ['./recipients-people-section.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, PeoplePickerComponent],
})
export class ActionPreviewRecipientsPeopleSectionComponent implements OnChanges {
    @Input({ required: true }) section!: ActionPreviewRecipientsPeopleSection;
    @Input() pendingEdit: ActionPreviewEdits['recipients'] | null = null;

    @Output() readonly edit = new EventEmitter<NonNullable<ActionPreviewEdits['recipients']>>();

    existingPersonIds: number[] = [];
    selectionCount = 0;

    constructor(private _cdr: ChangeDetectorRef) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['section']) {
            this.existingPersonIds = this._computeExistingPersonIds();
            this.selectionCount = this.existingPersonIds.length;
        }
    }

    private _computeExistingPersonIds(): number[] {
        // Expects `person-{id}` items only. Pre-selected `email:{addr}` items are
        // not surfaced today (SD-7239 ships with nothing pre-selected); if a future
        // ticket pre-seeds emails, track them here too or the diff will under-report
        // removals.
        return (this.section?.items ?? [])
            .map((item) => {
                const match = /^person-(\d+)$/.exec(item.id);
                return match ? Number(match[1]) : null;
            })
            .filter((id): id is number => id !== null);
    }

    onSelectionChange(selections: PickerSelection[]): void {
        const existing = new Set(this.existingPersonIds);

        const personIds = selections
            .filter((s): s is { type: 'person'; person: PickerPerson } => s.type === 'person')
            .map((s) => s.person.id);
        const emails = selections
            .filter((s): s is { type: 'email'; email: string; valid: boolean } => s.type === 'email')
            .filter((s) => s.valid)
            .map((s) => s.email);

        const selected = new Set(personIds);

        const addedIds = [
            ...personIds.filter((id) => !existing.has(id)).map((id) => `person-${id}`),
            ...emails.map((email) => `email:${email}`),
        ];
        const removedIds = Array.from(existing)
            .filter((id) => !selected.has(id))
            .map((id) => `person-${id}`);

        this.selectionCount = selected.size + emails.length;

        const next: NonNullable<ActionPreviewEdits['recipients']> = { ...this.pendingEdit };
        if (addedIds.length > 0) {
            next.added_ids = addedIds;
        } else {
            delete next.added_ids;
        }
        if (removedIds.length > 0) {
            next.removed_ids = removedIds;
        } else {
            delete next.removed_ids;
        }
        this.edit.emit(next);
        this._cdr.markForCheck();
    }
}
